import { z } from "zod";
import type { CaptureGrant, CaptureShare } from "./capture";
import { REGIONS, type Region } from "./regions";

export const healthResponseSchema = z.object({
  status: z.literal("ok"),
  service: z.literal("api"),
  version: z.string().min(1),
});

export type HealthResponse = z.infer<typeof healthResponseSchema>;

export const regionSchema = z.enum(REGIONS);
export type { Region };

/** Organization role, minted by the auth service into regional tokens. */
export const orgRoleSchema = z.enum(["owner", "admin", "member"]);
export type OrgRole = z.infer<typeof orgRoleSchema>;

const identifierSchema = z
  .string()
  .min(1)
  .refine((value) => value.trim().length > 0);

/** Parsing checks the shape only. Regional APIs must verify the JWT signature and scope. */
export const regionalAccessClaimsSchema = z.object({
  sub: identifierSchema,
  workspace_id: identifierSchema,
  region: regionSchema,
  token_use: z.literal("regional_access"),
  org_role: orgRoleSchema.optional(),
  iss: z.string().min(1),
  aud: z.literal("typedoc-api"),
  iat: z.number().int().nonnegative(),
  exp: z.number().int().nonnegative(),
});
export type RegionalAccessClaims = z.infer<typeof regionalAccessClaimsSchema>;

export const accessContextSchema = z.object({
  userId: identifierSchema,
  workspaceId: identifierSchema,
  region: regionSchema,
  role: orgRoleSchema.optional(),
});
export type AccessContext = z.infer<typeof accessContextSchema>;

/**
 * Where the operating system stands on letting the desktop app record the screen, hear the mic, or
 * follow what is clicked and typed (Accessibility). `unknown` means the system asks no such
 * question: the screen and Accessibility on Windows, everything on Linux.
 */
export type MediaAccess = "granted" | "denied" | "not-asked" | "restricted" | "unknown";
export type RecordingPermissions = {
  readonly screen: MediaAccess;
  readonly microphone: MediaAccess;
  /**
   * Asked for with the other two, so capture can follow clicks and keys without another prompt
   * later. A shell from before it says nothing, which reads as nothing to wait for.
   */
  readonly accessibility?: MediaAccess;
};

/** A part of the island window that takes clicks, in the window's CSS pixels from its top left. */
export type IslandRect = {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
};

/**
 * What the island tells the shell about its recording, for the tray. The session decides it; the
 * shell only words it. `notice` is a limit the session has reached: half an hour recorded, a quarter
 * of an hour paused (Abacor asks whether to finish), or the hour's stop.
 */
export type IslandStatus = {
  readonly mode: "new" | "live" | "paused" | "finished" | "stopped";
  readonly notice: "half-hour" | "paused-long" | "stopped" | null;
};

/**
 * A global shortcut pressed while the island is open: ⌥Space types to Abacor, ⌥⇧Space pauses.
 * On Windows they are Ctrl+Alt+Space and Ctrl+Alt+Shift+Space (`islandShortcutKeys` in
 * `@typedoc/contracts/desktop`).
 */
export type IslandShortcut = "type" | "pause" | "mic";

/**
 * What the island hands the shell to capture its recording's screens: the regional API base the
 * page uses for this workspace and a grant that adds screens to this one recording. Never a
 * sign-in token.
 */
export type CaptureStart = {
  readonly recordingId: string;
  readonly workspaceId: string;
  readonly apiBase: string;
  readonly grant: CaptureGrant;
  /**
   * Since bridge version 5: what Abacor sees (ENG-1031). Without it the shell shares the display
   * the person is working on, as version 4 did.
   */
  readonly share?: CaptureShare;
  /** Since bridge version 5: offer this choice first at the next recording ("Use this next time"). */
  readonly remember?: boolean;
};

/** A display as the island offers it (ENG-1031). */
export type ShareableDisplay = {
  /** The display's system ID, as a share names it. */
  readonly id: number;
  readonly name: string;
  /** In points. */
  readonly width: number;
  readonly height: number;
  /** The apps with a window on it, front first. */
  readonly apps: readonly string[];
  /**
   * A small picture of the display as Abacor would see it, never-seen apps and private windows
   * left out: a JPEG data URL, or null when none could be taken.
   */
  readonly thumbnail: string | null;
};

/** An app with a window open, as the island offers it (ENG-1031). Never-seen apps are not offered. */
export type ShareableApp = {
  readonly bundleId: string;
  readonly name: string;
  /** Its icon as a PNG data URL, or null. */
  readonly icon: string | null;
  /** Its front window's title, or empty; never a private window's. */
  readonly window: string;
  /** How many windows it has open, private windows left out. */
  readonly windows: number;
};

/** The choice the person asked to be offered first next time ("Use this next time"). */
export type RememberedShare =
  | { readonly kind: "display" }
  | { readonly kind: "apps"; readonly bundleIds: readonly string[] };

/** What the island can offer to share now (ENG-1031). */
export type Shareable = {
  readonly displays: readonly ShareableDisplay[];
  readonly apps: readonly ShareableApp[];
  /** The display the person is working on: the one preselected. */
  readonly working: number | null;
  readonly remembered: RememberedShare | null;
};

/**
 * The person is in something capture does not see (ENG-1031): an app outside the shared set, a
 * never-seen app, a private window, or another display. `offer` is true the first time they move
 * into an app that could be added, unless they chose to keep it out.
 */
export type CaptureOutside = {
  readonly app: string;
  readonly bundleId: string | null;
  readonly offer: boolean;
};

/** What capture shares now and whether the person is outside it, for the island to say. */
export type CaptureStatus = {
  /** Null: the display the person works on (a start without a share). */
  readonly share: CaptureShare | null;
  readonly outside: CaptureOutside | null;
  /**
   * Since bridge version 6 (ENG-1026): the person is in a password or card field, which is covered
   * and not recorded, so the island says "Not recording this field" before the tape does. Absent
   * from older shells, where the island follows the tape alone.
   */
  readonly masked?: boolean;
};

/**
 * Whether the shell started capturing: not without Screen Recording and Accessibility allowed
 * (`permissions`), off macOS (`unsupported`), or to an API base it does not send to (`api-base`).
 */
export type CaptureStartAnswer =
  | { readonly started: true }
  | { readonly started: false; readonly reason: "permissions" | "unsupported" | "api-base" };

/** Metadata and named capabilities exposed by the native preload, never a general IPC bridge. */
export type DesktopBridge = {
  /**
   * Version 8 (ENG-1017) adds no member: the island's window may use the mic, for Abacor's voice.
   * An older shell refuses every device to hosted pages, so the web asks for voice only from 8.
   */
  readonly bridgeVersion: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
  readonly appVersion: string;
  readonly platform: string;
  /**
   * In the island's window, true in the unpacked development build only (2 November 2026): the
   * island then offers choices for engineers, such as how Abacor asks. Absent in older shells.
   */
  readonly development?: boolean;
  /**
   * Since bridge version 2: opens a provider's sign-in page in the system browser. The shell
   * opens only approved sign-in addresses and silently ignores anything else. Absent in older
   * shells, so check for the function rather than trusting the version.
   */
  readonly openSignIn?: (url: string) => void;
  /**
   * Since bridge version 3, in the app's own window: opens the island on a recording the page has
   * just started, in a floating window of its own, or brings it back. Until the screen and the mic
   * are allowed, the shell opens its recording setup instead. Where this is missing the screen
   * cannot be recorded.
   */
  readonly showIsland?: (recordingId: string) => void;
  /**
   * Since bridge version 10, in the app's own window: readies the island's window, hidden, for the
   * next recording, so it shows at once. Asked once the person is signed in to a workspace.
   */
  readonly prepareIsland?: () => void;
  /**
   * Since bridge version 3, in the app's own window: the tray's Start a recording, delivered to the
   * page that will start the chat for it. Without a listener the shell opens a new chat itself.
   * The answer stops listening.
   */
  readonly onStartRecording?: (listener: () => void) => () => void;
  /** Since bridge version 3, in either window: screen and mic permission, as the system has it. */
  readonly recordingPermissions?: () => Promise<RecordingPermissions>;
  /**
   * Since bridge version 3, in the app's own window: opens the recording setup, where the screen
   * and the mic are allowed. A recording waits for both, so the page opens this instead.
   */
  readonly openRecordingSetup?: () => void;
  /**
   * Since bridge version 3, in the island's window only: the parts of the window that take clicks.
   * Everywhere else clicks pass through to whatever is under it.
   */
  readonly setIslandShape?: (rects: readonly IslandRect[]) => void;
  /** Since bridge version 3, in the island's window only: the recording's state, for the tray. */
  readonly setIslandStatus?: (status: IslandStatus) => void;
  /**
   * Since bridge version 10, in the island's window only: the recording the island shows, null when
   * it is hidden and waiting for the next. A page that listens is kept between recordings and told
   * the next one in place, without loading again. The answer stops listening.
   */
  readonly onIslandRecording?: (listener: (recordingId: string | null) => void) => () => void;
  /** Since bridge version 3, in the island's window only: the global shortcuts. */
  readonly onIslandShortcut?: (listener: (shortcut: IslandShortcut) => void) => () => void;
  /**
   * Since bridge version 3, in the island's window only: closes it. This ends nothing: a recording
   * still going stays so, and the chat's card opens the island on it again.
   */
  readonly closeIsland?: () => void;
  /**
   * Since bridge version 4, in the island's window only: starts capturing the recording's screens
   * with its grant, or carries on with a fresh one when it is already going. Answers null when the
   * shell refused the call. Absent in older shells, which record without screens.
   */
  readonly startCapture?: (start: CaptureStart) => Promise<CaptureStartAnswer | null>;
  /** Since bridge version 4, in the island's window only: a fresh grant before the one held runs out. */
  readonly setCaptureGrant?: (grant: CaptureGrant) => void;
  /** Since bridge version 4, in the island's window only: nothing is kept until it resumes. */
  readonly pauseCapture?: () => void;
  /** Since bridge version 4, in the island's window only. */
  readonly resumeCapture?: () => void;
  /** Since bridge version 4, in the island's window only: stops capturing; what was kept is still sent. */
  readonly stopCapture?: () => void;
  /**
   * Since bridge version 7, in the island's window only (ENG-1025): the person started talking, so
   * capture keeps the screen they are looking at.
   */
  readonly captureSpeech?: () => void;
  /**
   * Since bridge version 5, in the island's window only: the displays and the apps with a window
   * open that the person may share (ENG-1031), never-seen apps left out. Null when the shell cannot
   * say (no Screen Recording, not macOS). Absent in older shells, which share the display.
   */
  readonly shareableContent?: () => Promise<Shareable | null>;
  /** Since bridge version 5, in the island's window only: what the recording being captured shares now. */
  readonly setCaptureShare?: (share: CaptureShare) => void;
  /** Since bridge version 5, in the island's window only: Keep it out, for this recording. */
  readonly keepOutOfCapture?: (bundleId: string) => void;
  /**
   * Since bridge version 5, in the island's window only: what is shared and whether the person is
   * outside it, first as it is now, then on every change. Null while no capture is going, so the
   * island knows to ask what to share.
   */
  readonly onCaptureStatus?: (listener: (status: CaptureStatus | null) => void) => () => void;
};
